import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Search, MoreHorizontal, Eye, UserRound, Briefcase, MailCheck, Send } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import TablePagination from "../../components/TablePagination";
import FilterChips from "../../components/FilterChips";
import { fmtDate } from "../../lib/format";

export const ROLE = {
  admin: { label: "Pentadbir", cls: "bg-[#10056E] hover:bg-[#10056E]" },
  operator: { label: "Pengendali", cls: "bg-[#198754] hover:bg-[#198754]" },
  driver: { label: "Staf", cls: "bg-slate-500 hover:bg-slate-500" },
  pemandu: { label: "Pemandu", cls: "bg-sky-600 hover:bg-sky-600" },
};

const EMPTY = { name: "", staff_id: "", email: "", role: "driver", phone: "", unit_id: "", nric: "" };

export const ROLE_DESC = {
  admin: "Akses penuh — tetapan, pengguna, kelulusan & laporan",
  operator: "Proses tempahan, tugaskan kenderaan & pantau fleet",
  driver: "Mohon tempahan & log perjalanan sendiri",
  pemandu: "Menerima tugasan pemanduan & lihat tugasan sendiri",
};

export default function UsersSettings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [units, setUnits] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("semua");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => api.get("/users").then((r) => setUsers(r.data)), []);
  useEffect(() => { load(); api.get("/units").then((r) => setUnits(r.data)); }, [load]);
  useEffect(() => { setPage(1); }, [search, roleFilter]);

  const filtered = users
    .filter((u) => {
      const q = search.toLowerCase();
      const matches = !q || `${u.name} ${u.email} ${u.staff_id || ""}`.toLowerCase().includes(q);
      return matches && (roleFilter === "semua" || u.role === roleFilter);
    })
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const paged = filtered.slice((page - 1) * perPage, page * perPage);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", { ...form, unit_id: form.unit_id || null, staff_id: form.staff_id || null, nric: form.nric || null });
      toast.success(`Pengguna dicipta — emel pengaktifan dihantar ke ${form.email}`);
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const resendActivation = async (id) => {
    try {
      await api.post(`/users/${id}/resend-activation`);
      toast.success("Emel pengaktifan dihantar semula");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Padam pengguna ini?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Pengguna dipadam");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-4" data-testid="users-page">
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b flex flex-wrap items-center justify-between gap-3">
          <FilterChips
            value={roleFilter}
            onChange={setRoleFilter}
            testId="user-role-chips"
            options={[
              { value: "semua", label: "Semua", count: users.length },
              ...Object.entries(ROLE).map(([k, v]) => ({ value: k, label: v.label, count: users.filter((x) => x.role === k).length })),
            ]}
          />
          <div className="flex items-center gap-2">
            <div className="relative w-56">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9 h-9" placeholder="Cari nama atau emel..." value={search}
                onChange={(e) => setSearch(e.target.value)} data-testid="user-search-input" />
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="h-9 bg-[#10056E] hover:bg-[#2a17a8] shrink-0" data-testid="add-user-btn">
                  <Plus className="h-4 w-4 mr-1.5" /> Tambah Pengguna
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white max-w-xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Tambah Pengguna Baharu</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-5">
                  <div className="flex items-start gap-2.5 bg-indigo-50 border border-indigo-200 rounded-lg p-3" data-testid="user-activation-info">
                    <MailCheck className="h-4 w-4 text-[#10056E] shrink-0 mt-0.5" />
                    <p className="text-xs text-[#10056E]">
                      Tiada kata laluan diperlukan. Pengguna akan menerima <b>emel pengaktifan</b> untuk menetapkan
                      kata laluan sendiri semasa log masuk kali pertama (pautan sah 72 jam).
                    </p>
                  </div>

                  <div className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground flex items-center gap-1.5">
                      <UserRound className="h-3.5 w-3.5" /> Maklumat Peribadi
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label>Nama Penuh <span className="text-[#DC3545]">*</span></Label>
                        <Input data-testid="user-name-input" required value={form.name} placeholder="Cth: Ahmad bin Abdullah"
                          onChange={(e) => setForm({ ...form, name: e.target.value })} />
                      </div>
                      <div className="space-y-1.5">
                        <Label>ID Staf</Label>
                        <Input data-testid="user-staffid-input" value={form.staff_id} placeholder="Cth: MBDK-0123"
                          onChange={(e) => setForm({ ...form, staff_id: e.target.value })} />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Emel <span className="text-[#DC3545]">*</span></Label>
                        <Input data-testid="user-email-input" type="email" required value={form.email} placeholder="nama@agensi.gov.my"
                          onChange={(e) => setForm({ ...form, email: e.target.value })} />
                      </div>
                      <div className="space-y-1.5">
                        <Label>No. Telefon</Label>
                        <Input data-testid="user-phone-input" value={form.phone} placeholder="012-3456789"
                          onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                      </div>
                      <div className="space-y-1.5 sm:col-span-2">
                        <Label>No. Kad Pengenalan (MyKad/MyTentera/MyPDRM)</Label>
                        <Input data-testid="user-nric-input" value={form.nric} placeholder="Cth: 900101-01-1234"
                          onChange={(e) => setForm({ ...form, nric: e.target.value })} />
                        <p className="text-[11px] text-muted-foreground">Pengecam utama untuk log masuk MyDigital ID SSO.</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground flex items-center gap-1.5">
                      <Briefcase className="h-3.5 w-3.5" /> Penugasan
                    </p>
                    <div className="space-y-1.5">
                      <Label>Peranan <span className="text-[#DC3545]">*</span></Label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="user-role-options">
                        {Object.entries(ROLE).map(([k, v]) => (
                          <button
                            key={k}
                            type="button"
                            onClick={() => setForm({ ...form, role: k })}
                            data-testid={`user-role-option-${k}`}
                            className={`text-left border rounded-lg p-3 transition-colors ${
                              form.role === k
                                ? "border-[#10056E] ring-1 ring-[#10056E] bg-indigo-50/50"
                                : "border-slate-200 hover:border-[#10056E]/50"
                            }`}
                          >
                            <p className="text-sm font-bold">{v.label}</p>
                            <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{ROLE_DESC[k]}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Bahagian / Unit</Label>
                      <Select value={form.unit_id} onValueChange={(v) => setForm({ ...form, unit_id: v })}>
                        <SelectTrigger data-testid="user-unit-select"><SelectValue placeholder="Pilih Bahagian/Unit" /></SelectTrigger>
                        <SelectContent className="bg-white max-h-56">
                          {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button type="submit" className="w-full h-11 bg-[#10056E] hover:bg-[#2a17a8]" data-testid="user-submit-btn">
                    <Send className="h-4 w-4 mr-2" /> Cipta Pengguna & Hantar Emel Pengaktifan
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/70">
              <TableHead className="w-14">Foto</TableHead>
              <TableHead>Nama</TableHead>
              <TableHead>ID Staf</TableHead>
              <TableHead>Emel</TableHead>
              <TableHead>Peranan</TableHead>
              <TableHead>Bahagian/Unit</TableHead>
              <TableHead>Telefon</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Didaftar</TableHead>
              <TableHead className="text-right">Tindakan</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.length === 0 && (
              <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-10">Tiada pengguna dijumpai.</TableCell></TableRow>
            )}
            {paged.map((u) => (
              <TableRow key={u.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/tetapan/pengguna/${u.id}`)} data-testid={`user-row-${u.email}`}>
                <TableCell data-testid={`user-thumb-${u.email}`}>
                  {u.photo_thumb ? (
                    <img src={u.photo_thumb} alt={u.name} className="h-9 w-9 rounded-full object-cover border" loading="lazy" />
                  ) : (
                    <span className="h-9 w-9 rounded-full bg-[#10056E]/10 text-[#10056E] text-xs font-bold flex items-center justify-center">
                      {u.name?.split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase()}
                    </span>
                  )}
                </TableCell>
                <TableCell className="font-semibold">{u.name}</TableCell>
                <TableCell className="text-sm">{u.staff_id || "-"}</TableCell>
                <TableCell className="text-sm">{u.email}</TableCell>
                <TableCell><Badge className={ROLE[u.role]?.cls}>{ROLE[u.role]?.label}</Badge></TableCell>
                <TableCell className="text-sm">{u.unit_name || "-"}</TableCell>
                <TableCell className="text-sm">{u.phone || "-"}</TableCell>
                <TableCell>
                  {u.status === "belum_aktif" ? (
                    <Badge variant="outline" className="border-amber-400 text-amber-700 bg-amber-50" data-testid={`user-status-${u.email}`}>Belum Aktif</Badge>
                  ) : (
                    <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50" data-testid={`user-status-${u.email}`}>Aktif</Badge>
                  )}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{fmtDate(u.created_at)}</TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`user-actions-${u.email}`}>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-white w-56">
                      <DropdownMenuItem className="cursor-pointer" onClick={() => navigate(`/tetapan/pengguna/${u.id}`)} data-testid={`user-view-${u.email}`}>
                        <Eye className="h-4 w-4 mr-2" /> Lihat & Edit
                      </DropdownMenuItem>
                      {u.status === "belum_aktif" && (
                        <DropdownMenuItem className="cursor-pointer" onClick={() => resendActivation(u.id)} data-testid={`user-resend-${u.email}`}>
                          <Send className="h-4 w-4 mr-2" /> Hantar Semula Pengaktifan
                        </DropdownMenuItem>
                      )}
                      {u.id !== user?.id && (
                        <DropdownMenuItem className="cursor-pointer text-[#DC3545]" onClick={() => remove(u.id)} data-testid={`user-delete-${u.email}`}>
                          <Trash2 className="h-4 w-4 mr-2" /> Padam
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination page={page} setPage={setPage} perPage={perPage} setPerPage={setPerPage} total={filtered.length} />
      </div>
    </div>
  );
}
